import React, { useEffect, useState } from 'react';
import { Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { getDatabase, ref, get } from 'firebase/database';
import { useAuth } from '../firebase';
import { getAuth, signOut } from 'firebase/auth';
import { Link,useNavigate  } from 'react-router-dom';


const Account = () => {
  const { currentUser } = useAuth();
  const [userData, setUserData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        if (currentUser && currentUser.email) {
          const db = getDatabase();
          const usersRef = ref(db, 'users');
          const trainersRef = ref(db, 'trainers');

          const usersSnapshot = await get(usersRef);
          const trainersSnapshot = await get(trainersRef);

          if (usersSnapshot.exists()) {
            const users = Object.values(usersSnapshot.val());
            const user = users.find((user) => user.email === currentUser.email);

            if (user) {
              setUserData(user);
              return;
            }
          }

          if (trainersSnapshot.exists()) {
            const trainers = Object.values(trainersSnapshot.val());
            const trainer = trainers.find((trainer) => trainer.email === currentUser.email);

            if (trainer) {
              navigate('/trainer-account');
            }
          }
        }
      } catch (error) {
        console.log(error);
      }
    };

    fetchUserData();
  }, [currentUser]);


  const handleSignOut = async () => {
    try {
      const auth = getAuth();
      await signOut(auth);
  
      navigate('/login'); // Redirect to login page after sign out
    } catch (error) {
      console.log(error);
    }
  };



  const SignInPrompt = () => {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        mt={20}
        mb={30}
        border="2px solid"
        borderRadius="70px"
        p={2}
        mx={4}
        textAlign="center"
      >
        <Typography variant="h3">
          Seem's like you've signed out or haven't logged in to your account.
        </Typography>
        <Box mt={4}>
          <Typography>
            <Link to="/Login">Click here to Login</Link>
          </Typography>
        </Box>
      </Box>
    );
  };
  
  if (!userData) {
    return <SignInPrompt />;
  }

  const calculateBMI = () => {
    const weight = parseFloat(userData.weight);
    const height = parseFloat(userData.height) / 100; // Convert height to meters
    const bmi = weight / (height * height);
    return bmi.toFixed(2);
  };

  const getWeightCategory = (bmi) => {
    if (bmi < 18.5) {
      return 'Underweight';
    } else if (bmi < 25) {
      return 'Normal Weight';
    } else if (bmi < 30) {
      return 'Overweight';
    } else if (bmi < 35) {
      return 'Obesity Class 1';
    } else if (bmi < 40) {
      return 'Obesity Class 2';
    } else {
      return 'Obesity Class 3';
    }
  };

  const getWeightToReachNormalBMI = (bmi) => {
    const normalBMI = 22.5; // Assuming normal BMI value
    const height = parseFloat(userData.height) / 100; // Convert height to meters
    const weightToReachNormalBMI = normalBMI * (height * height);
    return weightToReachNormalBMI.toFixed(2);
  };

  const bmi = calculateBMI();
  const weightCategory = getWeightCategory(bmi);
  const weightToReachNormalBMI = getWeightToReachNormalBMI(bmi);

  const workoutPlan = getWorkoutPlan(weightCategory);

  function getWorkoutPlan(weightCategory) {
    switch (weightCategory) {
      case 'Underweight':
        return {
          Monday: {
            exercise: 'Rest',
          },
          Tuesday: {
            exercise: 'Jogging or cycling',
            targetedMuscles: 'Cardio (Full Body)',
            reps: '30 minutes',
          },
          Wednesday: {
            exercise: 'Squats, push-ups, lunges, dumbbell curls',
            targetedMuscles: 'Strength Training (Full Body)',
            reps: '3 sets of 10 reps',
          },
          Thursday: {
            exercise: 'Rest',
          },
          Friday: {
            exercise: ' Swimming or jump rope',
            targetedMuscles: 'Cardio (Full Body)',
            reps: '30 minutes',
          },
          Saturday: {
            exercise: 'Rest',
          },
          Sunday: {
            exercise: 'Rest',
          },
        };
      case 'Normal Weight':
        return {
          Monday: {
            exercise: 'Running or elliptical machine',
            targetedMuscles: 'Chest, shoulders, arms',
            reps: '30 minutes',
          },
          Tuesday: {
            exercise: 'Lat pulldowns, rows, deadlifts',
            targetedMuscles: 'Back, biceps, hamstrings',
            reps: '3 sets of 12 reps',
          },
          Wednesday: {
            exercise: 'Cycling or stair climbing',
            targetedMuscles: ' Legs, glutes',
            reps: '30 minutes',
          },
          Thursday: {
            exercise: 'Rest',
          },
          Friday: {
            exercise: 'Shoulder presses, lateral raises, upright rows',
            targetedMuscles: 'Shoulders, traps',
            reps: '3 sets of 12 reps',
          },
          Saturday: {
            exercise: 'Boxing or arm ergometer',
            targetedMuscles: 'Arms, core',
            reps: '30 minutes',
          },
          Sunday: {
            exercise: 'Rest',
          },
        };
      case 'Overweight':
        return {
          Monday: {
            exercise: 'Walking or jogging on an incline',
            targetedMuscles: 'Chest, shoulders, arms',
            reps: '30 minutes',
          },
          Tuesday: {
            exercise: 'Rowing machine or swimming',
            targetedMuscles: 'Back, core',
            reps: '30 minutes',
          },
          Wednesday: {
            exercise: 'Squats, lunges, leg press',
            targetedMuscles: 'Legs, glutes',
            reps: '3 sets of 10 reps',
          },
          Thursday: {
            exercise: 'Shoulder circuits or overhead kettlebell swings',
            targetedMuscles: 'Shoulders, traps',
            reps: '30 minutes',
          },
          Friday: {
            exercise: 'Rest',
          },
          Saturday: {
            exercise: 'Battle ropes or boxing',
            targetedMuscles: 'Arms, core',
            reps: '30 minutes',
          },
          Sunday: {
            exercise: 'Rest',
          },
        };
      case 'Obesity Class 1':
        return {
          Monday: {
            exercise: 'Running or elliptical machine',
            targetedMuscles: 'Chest, shoulders, arms',
            reps: '30 minutes',
          },
          Tuesday: {
            exercise: ' Lat pulldowns, rows, deadlifts',
            targetedMuscles: 'Back, biceps, hamstrings',
            reps: '3 sets of 12 reps',
          },
          Wednesday: {
            exercise: 'Cycling or stair climbing',
            targetedMuscles: 'Legs, glutes',
            reps: '30 minutes',
          },
          Thursday: {
            exercise: 'Shoulder presses, lateral raises, upright rows',
            targetedMuscles: 'Shoulders, traps',
            reps: '3 sets of 12 reps',
          },
          Friday: {
            exercise: 'Rest',
          },
          Saturday: {
            exercise: 'Boxing or arm ergometer',
            targetedMuscles: 'Arms, core',
            reps: '30 minutes',
          },
          Sunday: {
            exercise: 'Rest',
          },
        };
      case 'Obesity Class 2':
        return {
          Monday: {
            exercise: 'Running or elliptical machine',
            targetedMuscles: 'Chest, shoulders, arms',
            reps: '30 minutes',
          },
          Tuesday: {
            exercise: 'Lat pulldowns, rows, deadlifts',
            targetedMuscles: 'Back, biceps, hamstrings',
            reps: '3 sets of 12 reps',
          },
          Wednesday: {
            exercise: 'Cycling or stair climbing',
            targetedMuscles: 'Legs, glutes',
            reps: '30 minutes',
          },
          Thursday: {
            exercise: 'Shoulder presses, lateral raises, upright rows',
            targetedMuscles: 'Shoulders, traps',
            reps: '3 sets of 12 reps',
          },
          Friday: {
            exercise: 'Circuit training or high-intensity interval training (HIIT)',
            targetedMuscles: 'Full Body',
            reps: '30 minutes',
          },
          Saturday: {
            exercise: 'Rest',
          },
          Sunday: {
            exercise: 'Rest',
          },
        };
      case 'Obesity Class 3':
        return {
          Monday: {
            exercise: 'Running or elliptical machine',
            targetedMuscles: 'Chest, shoulders, arms',
            reps: '30 minutes',
          },
          Tuesday: {
            exercise: ' Lat pulldowns, rows, deadlifts',
            targetedMuscles: 'Back, biceps, hamstrings',
            reps: '3 sets of 12 reps',
          },
          Wednesday: {
            exercise: 'Cycling or stair climbing',
            targetedMuscles: 'Legs, glutes',
            reps: '30 minutes',
          },
          Thursday: {
            exercise: ' Shoulder presses, lateral raises, upright rows',
            targetedMuscles: 'Shoulders, traps',
            reps: '3 sets of 12 reps',
          },
          Friday: {
            exercise: 'Circuit training or high-intensity interval training (HIIT)',
            targetedMuscles: 'Full Body',
            reps: '30 minutes',
          },
          Saturday: {
            exercise: 'Circuit training or high-intensity interval training (HIIT)',
            targetedMuscles: 'Full Body',
            reps: '30 minutes',
          },
          Sunday: {
            exercise: 'Rest',
          },
        };
      default:
        return {};
    }
  }
  

  return (
    <Box sx={{ p: '20px' }}>
       <Box mt={10}>
      <Typography
        variant="h3"
        mb={6}
        backgroundColor="#ccdbee"
        textAlign="center"
        borderRadius="20px"
        p={2}
      >
        Account Details

        <Box display="flex" justifyContent="flex-end">
        <Button variant="contained" onClick={handleSignOut} sx={{ mr: 2 }}>
          Sign Out
        </Button>
      </Box>
      </Typography>
      
    </Box>
    

      {userData ? (
        <TableContainer>
          <Table sx={{ minWidth: 400 }} border='2px solid'>
            <TableHead >
              <TableRow >
                <TableCell sx={{textAlign: 'center',fontSize:'30px' ,backgroundColor:'#94b1e6'}}>
                  <strong>Field</strong>
                </TableCell>
                <TableCell sx={{textAlign: 'center',fontSize:'30px' ,backgroundColor:'#94b1e6'}}>
                  <strong >Value</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell sx={{textAlign: 'center',fontSize:'20px',backgroundColor:'#f9e076'}}>
                  <strong>Name</strong>
                </TableCell>
                <TableCell sx={{textAlign: 'center',fontSize:'20px',backgroundColor:'#f9e076'}}>{userData.name}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{textAlign: 'center',fontSize:'20px',backgroundColor:'#f9e076'}}>
                  <strong>Email</strong>
                </TableCell>
                <TableCell sx={{textAlign: 'center',fontSize:'20px',backgroundColor:'#f9e076'}}>{userData.email}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{textAlign: 'center',fontSize:'20px',backgroundColor:'#f9e076'}}>
                  <strong>Age</strong>
                </TableCell>
                <TableCell sx={{textAlign: 'center',fontSize:'20px',backgroundColor:'#f9e076'}}>{userData.age}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{textAlign: 'center',fontSize:'20px',backgroundColor:'#f9e076'}}>
                  <strong>Height</strong>
                </TableCell>
                <TableCell sx={{textAlign: 'center',fontSize:'20px',backgroundColor:'#f9e076'}}>{userData.height}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{textAlign: 'center',fontSize:'20px',backgroundColor:'#f9e076'}}>
                  <strong>Weight</strong>
                </TableCell>
                <TableCell sx={{textAlign: 'center',fontSize:'20px',backgroundColor:'#f9e076'}}>{userData.weight}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{textAlign: 'center',fontSize:'20px',backgroundColor:'#f9e076'}}>
                  <strong>Aim Weight</strong>
                </TableCell>
                <TableCell sx={{textAlign: 'center',fontSize:'20px',backgroundColor:'#f9e076'}}>{userData.aimWeight}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{textAlign: 'center',fontSize:'20px',backgroundColor:'#f9e076'}}>
                  <strong>BMI</strong>
                </TableCell>
                <TableCell sx={{textAlign: 'center',fontSize:'20px',backgroundColor:'#f9e076'}}>{bmi}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{textAlign: 'center',fontSize:'20px',backgroundColor:'#f9e076'}}>
                  <strong>Weight Category</strong>
                </TableCell>
                <TableCell sx={{textAlign: 'center',fontSize:'20px',backgroundColor:'#f9e076'}}>{weightCategory}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{textAlign: 'center',fontSize:'20px',backgroundColor:'#f9e076'}}>
                  <strong>Weight to Reach Normal BMI</strong>
                </TableCell>
                <TableCell sx={{textAlign: 'center',fontSize:'20px',backgroundColor:'#f9e076'}}>{weightToReachNormalBMI}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Link to={"Login"}></Link>
      )}

      {workoutPlan && (
        <Box mt={4}>
        <Typography variant='h3' mb={6} mt={15} backgroundColor='#ccdbee'
        textAlign={'center'}
        borderRadius={'20px'}
        p={'20px'}>Workout Plan</Typography>
        <TableContainer>
          <Table border='2px solid' >
            <TableHead >
              <TableRow >
                <TableCell sx={{textAlign: 'center',fontSize:'30px' ,backgroundColor:'#94b1e6'}}><strong>Day</strong></TableCell>
                <TableCell sx={{textAlign: 'center',fontSize:'30px' ,backgroundColor:'#94b1e6'}}><strong>Workout</strong></TableCell>
                <TableCell sx={{textAlign: 'center',fontSize:'30px' ,backgroundColor:'#94b1e6'}}><strong>Targeted Muscles</strong></TableCell>
                <TableCell sx={{textAlign: 'center',fontSize:'30px' ,backgroundColor:'#94b1e6'}}><strong>Reps</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(workoutPlan).map(([day, workout]) => (
                <TableRow key={day}>
                  <TableCell sx={{textAlign: 'center',fontSize:'20px',backgroundColor:'#f9e076'}}>{day}</TableCell>
                  <TableCell sx={{textAlign: 'center',fontSize:'20px',backgroundColor:'#f9e076'}}>{workout.exercise}</TableCell>
                  <TableCell sx={{textAlign: 'center',fontSize:'20px',backgroundColor:'#f9e076'}}>{workout.targetedMuscles}</TableCell>
                  <TableCell sx={{textAlign: 'center',fontSize:'20px',backgroundColor:'#f9e076'}}>{workout.reps}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Typography variant='h3' mb={6} mt={15} backgroundColor='#ff9999'
        textAlign={'center'}
        borderRadius={'20px'}
        p={'20px'}>Note:
          <Typography variant='h6' backgroundColor='#ccdbee' borderRadius={'20px'} p={'20px'} mt={4} mb={4} ml={6} mr={6}>
            1. This is a basic exercise plan suggested for you. <br/><br/>
            2. You can find more exercises at <Link to='/'>Home</Link> as per need. <br/><br/>
            3. Please consult an expert before doing any exercise.
           </Typography>
        </Typography>
        
      </Box>
      
      )}
    </Box>
  );
};

export default Account;
